class MobileNavTrigger extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("click", () => {
      this.toggleMobileMenu();
    });
  }

  toggleMobileMenu() {
    const menu = document.querySelector("#mobile-nav");
    if (menu) {
      const isOpen = menu.classList.contains("open");
      if (isOpen) {
        this.closeMobileMenu(menu);
      } else {
        this.openMobileMenu(menu);
      }
    }
  } 

  openMobileMenu(menu) {
    menu.classList.add("open");
    document.body.classList.add("mobile-menu-open");
    trapFocus(menu);
  }

  closeMobileMenu(menu) {
    menu.classList.remove("open");
    document.body.classList.remove("mobile-menu-open");
    removeTrapFocus(menu);
  }
}

customElements.define("mobile-nav-trigger", MobileNavTrigger);


// MobileNav class for rendering and initializing the mobile menu
class MobileNav extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = null;
  }

  connectedCallback() {
    this.render();
  } 

  async render() {
    const requestMobileLayout = await fetch(
      `https://www.vapesuperstore.co.uk/pages/about?view=mobile-menu`
    );
    const mobileLayoutData = await requestMobileLayout.text();

    let p = new DOMParser();
    let doc = p.parseFromString(mobileLayoutData, "text/html");
    let template = doc.querySelector("#mobile-raw-data");
    let injectData = template.querySelector("#menu-drawer");

    // Inject the menu into the DOM
    this.appendChild(injectData);

    // Now trigger MenuDrawer initialization
    this.initializeMenuDrawer();
  }

  initializeMenuDrawer() {
    const menuDrawerElement = this.querySelector('#menu-drawer');
    if (menuDrawerElement) {
      new MenuDrawer(menuDrawerElement);
    }
  }
}

customElements.define("mobile-nav", MobileNav);

// MenuDrawer class for managing the menu interactions
class MenuDrawer {
  constructor(menuDrawerElement) {
    this.menuDrawerElement = menuDrawerElement;
    this.mainDetailsToggle = this.menuDrawerElement.querySelector('details');

    // console.log('MenuDrawer constructor');
    // console.log('mainDetailsToggle:', this.mainDetailsToggle);

    if (navigator.platform === 'iPhone')
      document.documentElement.style.setProperty(
        '--viewport-height',
        `${window.innerHeight}px`
      );

    this.addEventListeners();
  }

  addEventListeners() {
    this.menuDrawerElement.addEventListener('keyup', this.onKeyUp.bind(this));
    this.menuDrawerElement.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    const summaries = this.menuDrawerElement.querySelectorAll('summary');
    summaries.forEach((summary) => {
      summary.addEventListener('click', this.onSummaryClick.bind(this));
    });
    
    this.menuDrawerElement.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onCloseButtonClick.bind(this))
    );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(
        summaryElement.nextElementSibling,
        detailsElement.querySelector('button')
      );
      summaryElement.nextElementSibling.removeEventListener(
        'transitionend',
        addTrapFocus
      );
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) {
        event.preventDefault();
      }
      isOpen
        ? this.closeMenuDrawer(event, summaryElement)
        : this.openMenuDrawer(summaryElement);
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener(
              'transitionend',
              addTrapFocus
            );
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.menuDrawerElement.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');

    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle
      .querySelectorAll('.submenu-open')
      .forEach((submenu) => {
        submenu.classList.remove('submenu-open');
      });
    document.body.classList.remove(`overflow-hidden-${this.menuDrawerElement.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);
  }

  onFocusOut(event) {
    setTimeout(() => {
      if (
        this.mainDetailsToggle.hasAttribute('open') &&
        !this.mainDetailsToggle.contains(document.activeElement)
      )
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement
      .querySelector('summary')
      .setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(
            detailsElement.closest('details[open]'),
            detailsElement.querySelector('summary')
          );
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}